"use client";

import React, { useEffect, useState } from "react";
import "./dashboard.css";
import {
  check_auth,
  getCourseDetail,
  getStudentDetail,
  logoutStudent,
  get_course_progress,
} from "@/api";
import { useRouter } from "next/navigation";
import ClassSchedule from "./ClassSchedule";
import {
  BookOpen,
  CaretLeft,
  CaretRight,
  Chat,
  CheckCircle,
  SignOut,
  Trophy,
  UserCircle,
  UserCircleCheck,
} from "@phosphor-icons/react";
import toast from "react-hot-toast";
import LoadingSpinner from "../lib/LoadingSpinner";

// const courses_data = [
//   {
//     id: 1,
//     title: "Financial Modeling",
//     description:
//       "Financial Modeling is a fundamental concept in finance that helps you understand the relationship between financial assets, liabilities, and income. It is a crucial skill for anyone interested in investing, managing assets, or making informed financial decisions.",
//     cover_image: "/slide2_bg.svg",
//     progress: 50,
//   },
//   {
//     id: 2,
//     title: "Financial Modeling",
//     description:
//       "Financial Modeling is a fundamental concept in finance that helps you understand the relationship between financial assets, liabilities, and income. It is a crucial skill for anyone interested in investing, managing assets, or making informed financial decisions.",
//     cover_image: "/slide2_bg.svg",
//     progress: 70,
//   },
//   {
//     id: 3,
//     title: "Financial Modeling",
//     description:
//       "Financial Modeling is a fundamental concept in finance that helps you understand the relationship between financial assets, liabilities, and income. It is a crucial skill for anyone interested in investing, managing assets, or making informed financial decisions.",
//     cover_image: "/slide2_bg.svg",
//     progress: 40,
//   },
//   {
//     id: 4,
//     title: "Financial Modeling",
//     description:
//       "Financial Modeling is a fundamental concept in finance that helps you understand the relationship between financial assets, liabilities, and income. It is a crucial skill for anyone interested in investing, managing assets, or making informed financial decisions.",
//     cover_image: "/slide2_bg.svg",
//     progress: 40,
//   },
// ];

const StudentDashboard = () => {
  const [student, setStudent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const router = useRouter();
  const getCourseId = (course) =>
    course?.id || course?._id || course?.course_id || course?.slug || "";

  useEffect(() => {
    const handleResize = () => {
      setCollapsed(window.innerWidth < 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // useEffect(() => {
  //   const checkLoggedIn = async () => {
  //     const session = await check_auth();
  //     if (session) {
  //       router.replace("/dashboard");
  //     } else {
  //       setCheckingAuth(false);
  //       router.replace("/login");
  //       toast.error("Please login to access your dashboard");
  //     }
  //   };
  //   checkLoggedIn();
  // }, [router]);

  useEffect(() => {
    const checkLoggedIn = async () => {
      const session = await check_auth();
      if (!session) {
        router.replace("/login");
        toast.error("Please login to access your dashboard");
      } else {
        setCheckingAuth(false);
      }
    };
    checkLoggedIn();
  }, [router]);

  const handleLogout = async () => {
    try {
      await logoutStudent();
      router.replace("/");
      toast.success("You have successfully logged out.");
    } catch (err) {
      console.error("Logout failed:", err.message);
    }
  };

  useEffect(() => {
    const fetchStudentAndCourses = async () => {
      try {
        const studentData = await getStudentDetail();
        setStudent(studentData);

        if (studentData.purchased_courses?.length > 0) {
          const coursePromises = studentData.purchased_courses.map((courseId) =>
            getCourseDetail(courseId),
          );

          const progressPromises = studentData.purchased_courses.map((courseId) =>
            get_course_progress(courseId).catch((err) => {
              console.error(`Failed to fetch progress for ${courseId}:`, err);
              return null;
            })
          );

          const [courseResults, progressResults] = await Promise.all([
            Promise.allSettled(coursePromises),
            Promise.allSettled(progressPromises)
          ]);

          const validCourses = courseResults
            .filter((result) => result.status === "fulfilled")
            .map((result) => result.value);

          const progressMap = {};
          progressResults.forEach((result) => {
            if (result.status === "fulfilled" && result.value) {
              progressMap[result.value.course_id] = result.value.overall_progress_percentage;
            }
          });

          const coursesWithProgress = validCourses.map((c) => {
            const cid = c.id || c._id;
            return {
              ...c,
              progress: progressMap[cid] !== undefined ? progressMap[cid] : 0
            };
          });

          setCourses(coursesWithProgress);
        } else {
          setCourses([]);
        }
      } catch (err) {
        setError(err.message || "Failed to load student data.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentAndCourses();
  }, []);

  if (checkingAuth) return <LoadingSpinner />;

  return (
    <div className="w-full">

      <button
        className="fixed top-5 right-5 z-10 flex lg:hidden items-center gap-1 text-blue-500 bg-gray-100 text-sm py-1 px-2 rounded-full"
        onClick={handleLogout}
      >
        <SignOut
          size={16}
          className="cursor-pointer transition-colors duration-300"
        />
        Logout
      </button>
      <div className="flex min-h-screen">
        <aside
          className={`
    ${collapsed ? "w-[70px]" : "w-63"}
    bg-white shadow-xl flex flex-col justify-between fixed left-0 h-full pb-18 z-7
    transition-all duration-300 ease-in-out
  `}
        >
          <div className="relative">
            <button
              className="cursor-pointer hover:bg-blue-400 transition-all duration-300 flex gap-2 items-center absolute -right-5 top-10 shadow-md hover:shadow-lg text-white bg-[#0D6FEA] rounded-full p-2 z-10"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
            </button>

            <div className="text-center mb-6 p-6">
              {!collapsed && (
                <h2 className="text-xl font-semibold">
                  Welcome, {student?.name || "Student"}
                </h2>
              )}
            </div>

            <nav className="mt-10 w-full">
              <ul className="space-y-4">
                <li
                  className={`mx-3 px-4 py-3 rounded-xl cursor-pointer hover:bg-[#0D6FEA] hover:text-white transition-all duration-300 flex gap-3 items-center ${
                    collapsed ? "justify-center mx-1 px-0" : ""
                  } ${true ? "bg-[#0D6FEA] text-white shadow-md shadow-[#0d6fea]/20" : "text-gray-700"}`}
                >
                  <BookOpen size={24} className="flex-shrink-0" />
                  {!collapsed && <span className="font-semibold text-sm">My Courses</span>}
                </li>
              </ul>
            </nav>
          </div>
          <button
            className={`mx-3 mb-6 px-4 py-3 rounded-xl hidden lg:flex items-center gap-3 text-red-500 cursor-pointer transition-all duration-300 hover:bg-red-50 hover:text-red-600 ${
              collapsed ? "justify-center mx-1 px-0" : ""
            }`}
            onClick={handleLogout}
          >
            <SignOut size={24} className="flex-shrink-0" />
            {!collapsed && <span className="font-semibold text-sm">Logout</span>}
          </button>
        </aside>

        {/* {loading && <p>Loading...</p>}
            {error && <p className="error">{error}</p>} */}

        <div
          className={`py-18 px-10 flex w-full transition-all duration-300 z-6 ${
            collapsed ? "pl-25 lg:pl-40" : "pl-25 lg:pl-70"
          }`}
        >
          <div className="w-[100%]">
            <div className="sticky top-18 bg-white pb-4 mb-6 border-b border-gray-100">
              {collapsed && (
                <h2 className="text-xl font-bold text-gray-800 mb-1">
                  Welcome, {student?.name || "Student"}
                </h2>
              )}
              <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Purchased Courses</h1>
            </div>
            {courses.length > 0 ? (
              <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 list-none">
                {courses.map((course) => (
                  <li
                    key={getCourseId(course) || course?.title}
                    className="group w-full rounded-2xl border border-gray-100 shadow-md hover:shadow-xl hover:-translate-y-1 bg-white cursor-pointer transition-all duration-300 overflow-hidden flex flex-col"
                    onClick={() => {
                      const courseId = getCourseId(course);
                      if (courseId) router.push(`/learn/${courseId}`);
                    }}
                  >
                    <div className="w-full h-[130px] overflow-hidden relative">
                      <img
                        src={course.cover_image}
                        alt={course.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm text-[10px] font-bold text-[#0D6FEA] px-2.5 py-1 rounded-full shadow-sm">
                        {course.progress ?? 0}% Done
                      </div>
                    </div>
                    <div className="p-5 flex-grow flex flex-col justify-between">
                      <div>
                        <h4 className="font-bold text-[#0D6FEA] uppercase text-base mb-2 group-hover:text-blue-600 transition-colors duration-200">
                          {course.title}
                        </h4>
                        <p className="text-xs leading-[135%] text-gray-500 line-clamp-3 mb-4">
                          {course.description}
                        </p>
                      </div>
                      <div>
                        <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden mb-1.5">
                          <div
                            className="bg-gradient-to-r from-[#0D6FEA] to-[#3b82f6] h-full rounded-full transition-all duration-500"
                            style={{ width: `${course.progress ?? 0}%` }}
                          />
                        </div>
                        <p className="text-[10px] font-medium text-gray-400 text-right">
                          {course.progress}% complete
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No courses purchased yet.</p>
            )}
          </div>
        </div>
        {/* <ClassSchedule /> */}
      </div>
    </div>
  );
};

export default StudentDashboard;
